// Correcting one qualification test: open it (`?details=`), score each item,
// then reject it (under 5/10) or move it to the corrected tabs.
import { useState } from "react";
import type { SetURLSearchParams } from "react-router-dom";
import { testsApi } from "@/api/tests.api";
import { sortTestsByScore } from "../helpers";
import {
  CORRECTION_PASS_MARK,
  allItemsScored,
  buildSubmissionDetails,
  clampScore,
  scoreAverages,
  weightedTotal,
  type ItemScores,
  type SubmissionDetails,
} from "../logic/correction-scoring";
import type { GetPaidBase } from "./get-paid-context";

interface TestCorrectionInput extends Omit<GetPaidBase, "user"> {
  setSearchParams: SetURLSearchParams;
  pendingTests: any[];
  setPendingTests: React.Dispatch<React.SetStateAction<any[]>>;
  setCorrectedTests: React.Dispatch<React.SetStateAction<any[]>>;
  setActiveModTab: (tab: "PENDING" | "VALIDATED") => void;
}

export function useTestCorrection({ t, toast, setSearchParams, pendingTests, setPendingTests, setCorrectedTests, setActiveModTab }: TestCorrectionInput) {
  const [details, setDetails] = useState<SubmissionDetails | null>(null);
  const [itemScores, setItemScores] = useState<ItemScores>({});
  const [savingScores, setSavingScores] = useState(false);

  const setDetailsParam = (testId: string | undefined, open: boolean) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (open) next.set("details", testId as string);
      else next.delete("details");
      return next;
    }, { replace: true });
  };

  const loadSubmissionDetails = async (test: any) => {
    setDetails(buildSubmissionDetails(test));
    setItemScores({});
    setDetailsParam(test.id, true);
  };

  const closeDetails = () => {
    setDetails(null);
    setItemScores({});
    setDetailsParam(undefined, false);
  };

  const handleBackFromDetails = () => closeDetails();

  const setScore = (id: string, value: string) => {
    const v = clampScore(value);
    if (v === null) return;
    setItemScores((prev) => ({ ...prev, [id]: v }));
  };

  const averages = () => scoreAverages(details, itemScores);
  const totalWeighted = () => weightedTotal(averages());
  const canValidate = () => allItemsScored(details, itemScores);

  // Nothing is sent here: scores only reach the server with "finish".
  const handleSaveScores = async () => {
    setSavingScores(true);
    try {
      toast({ title: t.getPaid.scoresSaved, description: t.getPaid.scoresSavedDesc });
    } finally {
      setSavingScores(false);
    }
  };

  const handleFinishCorrection = async () => {
    if (!canValidate()) {
      toast({ title: t.getPaid.incomplete, description: t.getPaid.scoreAllItems, variant: "destructive" });
      return;
    }
    setSavingScores(true);
    try {
      const total = Math.round(totalWeighted() * 100) / 100;
      const correctedTest = pendingTests.find((it: any) => it.id === details?.testId);

      if (!details?.testId) {
        throw new Error(t.getPaid.testNotFound);
      }

      if (total < CORRECTION_PASS_MARK) {
        await testsApi.moderateSubmission(details.testId, "REJECTED", total);
        setPendingTests((prev: any[]) => prev.filter((it: any) => it.id !== details?.testId));
        setCorrectedTests((prev: any[]) => prev.filter((it: any) => it.id !== details?.testId));
        toast({ title: t.getPaid.rejectedTest, description: t.getPaid.rejectedTestDesc.replace("{score}", String(total)) });
        setActiveModTab("PENDING");
      } else if (correctedTest) {
        await testsApi.moderateSubmission(details.testId, {
          status: "PENDING",
          correctionStatus: "CORRECTED",
          score: total,
        });
        const correctedItem = {
          ...correctedTest,
          totalScore: total,
          status: "PENDING",
          correctionStatus: "CORRECTED",
          correctedAt: new Date().toISOString(),
        };
        setPendingTests((prev: any[]) => prev.filter((it: any) => it.id !== details?.testId));
        setCorrectedTests((prev: any[]) => sortTestsByScore([
          correctedItem,
          ...prev.filter((it: any) => it.id !== correctedItem.id),
        ]));
        toast({ title: t.getPaid.correctionCompleted, description: t.getPaid.correctionCompletedDesc.replace("{score}", String(total)) });
        setActiveModTab("VALIDATED");
      }

      closeDetails();
    } catch {
      toast({ title: t.error, description: t.getPaid.saveCorrectionFailed, variant: "destructive" });
    } finally {
      setSavingScores(false);
    }
  };

  return {
    details, itemScores, savingScores,
    loadSubmissionDetails, handleBackFromDetails,
    setScore, averages, totalWeighted, canValidate,
    handleSaveScores, handleFinishCorrection,
  };
}
